import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addProviderBankAccount,
  createProvider,
  deleteProviderBankAccount,
  getProvider,
  getProviders,
  updateProvider,
  updateProviderBankAccount,
} from '../api';
import ProviderFormFields from '../components/providers/ProviderFormFields';
import { formatRut } from '../utils/rut';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import {
  EMPTY_PROVIDER,
  createEmptyAccount,
  getProviderApiErrorMessage,
  validateProviderForm,
} from '../utils/provider';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { normalizePaginatedResponse } from '../utils/pagination';

const ProviderFormPage = () => {
  const navigate = useNavigate();
  const { branchId, id } = useParams();
  const selectedBranchId = branchId || localStorage.getItem('selectedBranchId');
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY_PROVIDER);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const { confirm } = useConfirmDialog();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allProviders, setAllProviders] = useState([]);
  const { user } = useCurrentUser();
  const canCreateProvider = hasPermission(user, 'provider.create');
  const canUpdateProvider = hasPermission(user, 'provider.update');
  const canEditProvider = isEdit ? canUpdateProvider : canCreateProvider;

  useEffect(() => {
    if (!isEdit) return;
    if (!canUpdateProvider) {
      setError('No tienes permisos para editar proveedores.');
      setLoading(false);
      return;
    }
    const fetchProvider = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getProvider(id, { includeDeleted: true });
        setForm({
          name: data.name || '',
          rut: data.rut || '',
          giro: data.giro || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
        });
        setBankAccounts(
          Array.isArray(data.ProviderBankAccounts) ? data.ProviderBankAccounts : []
        );
      } catch (err) {
        console.error(err);
        setError(getProviderApiErrorMessage(err, 'Error al cargar proveedor.'));
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id, isEdit, canUpdateProvider]);

  useEffect(() => {
    if (!canEditProvider) return;
    let mounted = true;

    const fetchAllProviders = async () => {
      try {
        const data = await getProviders({ includeDeleted: true, pageSize: 0 });
        const normalized = normalizePaginatedResponse(data);
        if (!mounted) return;
        setAllProviders(normalized.data || []);
      } catch (err) {
        console.error(err);
      }
    };

    fetchAllProviders();

    return () => {
      mounted = false;
    };
  }, [canEditProvider]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRutChange = (e) => {
    setForm((prev) => ({ ...prev, rut: formatRut(e.target.value) }));
  };

  const handleAccountChange = (index, field, value) => {
    setBankAccounts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddAccount = () => {
    if (!canEditProvider) return;
    setBankAccounts((prev) => [...prev, createEmptyAccount()]);
  };

  const handleRemoveAccount = async (account, index) => {
    if (!canEditProvider) return;
    if (!account.id) {
      setBankAccounts((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    const approved = await confirm({
      title: 'Eliminar cuenta',
      message: 'Eliminar esta cuenta bancaria?',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    try {
      await deleteProviderBankAccount(id, account.id);
      setBankAccounts((prev) => prev.filter((_, i) => i !== index));
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al eliminar cuenta bancaria.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canEditProvider) return;
    const {
      payload: providerPayload,
      accountPayloads,
      error: validationError,
    } = validateProviderForm({
      form,
      accounts: bankAccounts,
      providers: allProviders,
      currentProviderId: id,
      rejectPersistedEmptyAccount: isEdit,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateProvider(id, providerPayload);
        const updateRequests = [];
        const createRequests = [];
        accountPayloads.forEach((payload) => {
          if (payload.id) {
            const { id: accountId, ...accountPayload } = payload;
            updateRequests.push(updateProviderBankAccount(id, accountId, accountPayload));
          } else {
            createRequests.push(addProviderBankAccount(id, payload));
          }
        });
        await Promise.all([...updateRequests, ...createRequests]);
      } else {
        const createPayload = {
          ...providerPayload,
          bankAccounts: accountPayloads.map(({ id: _, ...accountPayload }) => accountPayload),
        };
        await createProvider(createPayload);
      }
      navigate(`/branches/${selectedBranchId}/providers`);
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al guardar proveedor.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-2">
          {isEdit ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        </h1>
        <p className="text-gray-600 mb-6">
          Completa los datos del proveedor y sus cuentas bancarias.
        </p>
        {loading ? (
          <p>
            <InlineLoading />
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && <p className="text-red-600">{error}</p>}
            <ProviderFormFields
              form={form}
              onChange={handleChange}
              onRutChange={handleRutChange}
              accounts={bankAccounts}
              onAccountAdd={handleAddAccount}
              onAccountRemove={handleRemoveAccount}
              onAccountChange={handleAccountChange}
              formatRut={formatRut}
              disabled={!canEditProvider}
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate(`/branches/${selectedBranchId}/providers`)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancelar
              </button>
              {canEditProvider && (
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-60"
                >
                  {saving ? <InlineLoading label="Guardando" /> : 'Guardar'}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProviderFormPage;
